import React, { useState, useEffect, useContext } from 'react';
import { approvalAPI } from '../services/api';
import { reportLoadError, notify } from '../utils/notify';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import { Check, X } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import './Approvals.css';

const Approvals = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'my-requests') {
        const response = await approvalAPI.getMyRequests();
        setMyRequests(response.data.requests);
      } else {
        const response = await approvalAPI.getAll({ status: activeTab });
        setRequests(response.data.requests);
      }
    } catch (error) {
      console.error('Error loading requests:', error);
      reportLoadError(error, t('Error loading approval requests'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    try {
      await approvalAPI.approve(selectedRequest.id, { notes: approveNotes });
      notify(t('Request approved successfully!'), 'success');
      setShowApproveModal(false);
      setSelectedRequest(null);
      setApproveNotes('');
      loadData();
    } catch (error) {
      console.error('Error approving request:', error);
      notify(error.response?.data?.message || t('Error approving request'), 'error');
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!rejectNotes.trim()) {
      notify(t('Please provide a reason for rejection'), 'warning');
      return;
    }

    try {
      await approvalAPI.reject(selectedRequest.id, { notes: rejectNotes });
      notify(t('Request rejected'), 'success');
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectNotes('');
      loadData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      notify(error.response?.data?.message || t('Error rejecting request'), 'error');
    }
  };

  const getRequestTypeLabel = (type) => {
    // Leave is the only type decided here (the others were retired).
    const labels = {
      leave: 'Leave'
    };
    return t(labels[type] || type);
  };

  const getRequestTypeBadge = (type) => (
    <StatusBadge status={type} className="appr-type">{type === 'leave' ? t('Leave') : t(type)}</StatusBadge>
  );

  const getStatusBadge = (status) => {
    const badges = {
      pending: t('Pending'),
      approved: t('Approved'),
      rejected: t('Rejected')
    };
    const known = badges[status] ? status : 'pending';

    return <StatusBadge status={known}>{badges[known]}</StatusBadge>;
  };

  const canApprove = ['super_admin', 'finance_manager', 'operations_manager'].includes(user?.role);
  const displayRequests = activeTab === 'my-requests' ? myRequests : requests;

  return (
    <div className="approvals-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Approval Requests')}</h1>
          <p className="page-sub">{t('Review and approve pending requests')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabbar">
        <button
          className={`tabbar__tab${activeTab === 'pending' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          {t('Pending ({count})', { count: requests.filter(r => r.status === 'pending').length })}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'approved' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('approved')}
        >
          {t('Approved')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'rejected' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('rejected')}
        >
          {t('Rejected')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'my-requests' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('my-requests')}
        >
          {t('My Requests')}
        </button>
      </div>

      {/* Requests List */}
      <div className="appr-content">
        {loading ? (
          <div className="empty">{t('Loading requests...')}</div>
        ) : displayRequests.length === 0 ? (
          <div className="empty">
            <p>{t('No requests found')}</p>
          </div>
        ) : (
          <div className="appr-list">
            {displayRequests.map(request => (
              <div key={request.id} className="appr-card">
                <div className="appr-card__head">
                  <div className="appr-badges">
                    {getRequestTypeBadge(request.request_type)}
                    {getStatusBadge(request.status)}
                  </div>
                  <span className="appr-card__date">
                    {new Date(request.created_at).toLocaleDateString(locale)}
                  </span>
                </div>

                <div className="appr-card__body">
                  <div className="appr-card__info">
                    <p className="appr-requester">
                      <strong>{t('Requested by:')}</strong> {request.requester_first_name} {request.requester_last_name}
                      <br />
                      <small>{request.requester_email}</small>
                    </p>
                    
                    <div className="appr-reason">
                      <strong>{t('Reason:')}</strong>
                      <p>{request.reason}</p>
                    </div>

                    {request.old_data && (
                      <div className="appr-data">
                        <strong>{t('Current Data:')}</strong>
                        <pre>{JSON.stringify(request.old_data, null, 2)}</pre>
                      </div>
                    )}

                    {request.new_data && (
                      <div className="appr-data">
                        <strong>{t('Requested Changes:')}</strong>
                        <pre>{JSON.stringify(request.new_data, null, 2)}</pre>
                      </div>
                    )}

                    {request.status !== 'pending' && request.approver_first_name && (
                      <div className="appr-decision">
                        <strong>
                          {request.status === 'approved' ? t('Approved by:') : t('Rejected by:')}
                        </strong> {request.approver_first_name} {request.approver_last_name}
                        {request.notes && (
                          <>
                            <br />
                            <small><strong>{t('Notes:')}</strong> {request.notes}</small>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {request.status === 'pending' && canApprove && activeTab !== 'my-requests' && (
                    <div className="appr-actions">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowApproveModal(true);
                        }}
                        className="btn btn--primary appr-approve"
                      >
                        <Check aria-hidden="true" /> {t('Approve')}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowRejectModal(true);
                        }}
                        className="btn btn--secondary appr-reject"
                      >
                        <X aria-hidden="true" /> {t('Reject')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <Modal open title={t('Approve Request')} onClose={() => setShowApproveModal(false)} size="md">

            <div className="appr-dialog">
              <p><strong>{t('Type:')}</strong> {getRequestTypeLabel(selectedRequest.request_type)}</p>
              <p><strong>{t('Requested by:')}</strong> {selectedRequest.requester_first_name} {selectedRequest.requester_last_name}</p>
              <p><strong>{t('Reason:')}</strong> {selectedRequest.reason}</p>

              <form onSubmit={handleApprove}>
                <div className="appr-field">
                  <label className="field-label">{t('Approval Notes (Optional)')}</label>
                  <textarea className="textarea"
                    value={approveNotes}
                    onChange={(e) => setApproveNotes(e.target.value)}
                    rows="3"
                    placeholder={t('Add any notes...')}
                  />
                </div>

                <div className="appr-form-actions">
                  <button type="button" onClick={() => setShowApproveModal(false)} className="btn btn--secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn--primary appr-submit-approve">
                    <Check aria-hidden="true" /> {t('Approve')}
                  </button>
                </div>
              </form>
            </div>
        </Modal>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <Modal open title={t('Reject Request')} onClose={() => setShowRejectModal(false)} size="md">

            <div className="appr-dialog">
              <p><strong>{t('Type:')}</strong> {getRequestTypeLabel(selectedRequest.request_type)}</p>
              <p><strong>{t('Requested by:')}</strong> {selectedRequest.requester_first_name} {selectedRequest.requester_last_name}</p>

              <form onSubmit={handleReject}>
                <div className="appr-field">
                  <label className="field-label">{t('Reason for Rejection *')}</label>
                  <textarea className="textarea"
                    value={rejectNotes}
                    onChange={(e) => setRejectNotes(e.target.value)}
                    rows="4"
                    placeholder={t('Please provide a reason for rejection...')}
                    required
                  />
                </div>

                <div className="appr-form-actions">
                  <button type="button" onClick={() => setShowRejectModal(false)} className="btn btn--secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn--danger appr-submit-reject">
                    <X aria-hidden="true" /> {t('Reject')}
                  </button>
                </div>
              </form>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default Approvals;